// 入力フォームの初期値 (initialValues属性にセットする)
import { PagePath } from 'constants/pagePath';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastMessageLevel } from 'views/mutual/toast';
import { useToast } from 'views/mutual/toast/hooks';
import { SendSMSParam } from 'views/templates/sendSMS';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import { useReissuePasswordMutation, useSendSmsMutation, useSmsCertificateMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';

type LoginParamType = {
  account: string;
  phoneNumber: string;
};

export enum PageStep {
  INPUT_INFO = 1,
  CERTIFIBATE = 2,
  FIN = 3,
}

export const useReissuePassword = () => {
  const [provisionalToken, setProvisionalToken] = useState<string>('');
  const [pageStep, setPageStep] = useState<PageStep>(PageStep.INPUT_INFO);
  const navigate = useNavigate();
  const [reissuePasswordData, reissuePassword] = useReissuePasswordMutation();
  const { toastProps, showToast } = useToast();
  const { onSendSMS } = useSendSMS(provisionalToken, setProvisionalToken, showToast);
  const certificatePage = useCertificatePage(provisionalToken, setPageStep, showToast);

  const validate = (values: LoginParamType) => {
    const errors: Partial<{
      account: string;
      phoneNumber: string;
    }> = {};

    if (!values.account) {
      errors.account = '口座番号が未入力です';
    }

    if (!values.phoneNumber) {
      errors.phoneNumber = '電話番号が未入力です';
    } else if (!values.phoneNumber.match(/^[0-9]{11}$/)) {
      errors.phoneNumber = '11桁の半角数字のみで入力してください';
    }

    return errors;
  };

  const onSubmit = useOnceSubmit<LoginParamType>(
    {
      onSubmit: async (values) => {
        const ret = await reissuePassword({
          input: {
            Account: values.account,
            PhoneNumber: values.phoneNumber,
          },
        });

        if (ret.data && ret.data?.ReissuePassword?.Code !== 0) {
          showToast(ret.data?.ReissuePassword?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.ReissuePassword?.Code === 0) {
          setProvisionalToken(ret.data.ReissuePassword.ProvisionalToken!);
          setPageStep(PageStep.CERTIFIBATE);
        }
      },
    },
    [showToast, setProvisionalToken, setPageStep],
  );

  const formik = useFormik<LoginParamType>({
    initialValues: {
      account: '',
      phoneNumber: '',
    },
    onSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  const goTopPage = () => {
    navigate(PagePath.Login);
  };

  return { formik, pageStep, toastProps, certificatePage: { onSendSMS, ...certificatePage }, goTopPage };
};

const useSendSMS = (
  initialProvisionalToken: string,
  setProvisionalToken: React.Dispatch<React.SetStateAction<string>>,
  onShowToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await sendSMS({
          input: {
            Type: 1,
            ProvisionalToken: initialProvisionalToken,
          },
        });
        if (res.data && res.data?.SendSMS?.Code !== 0) {
          onShowToast(res.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          onShowToast('認証コードを送信しました', ToastMessageLevel.Info);
        }
      },
    },
    [sendSMS, initialProvisionalToken, onShowToast, setProvisionalToken],
  );

  return {
    onSendSMS,
  };
};

const useCertificatePage = (
  provisionalToken: string,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [smsCertificatelData, smsCertificate] = useSmsCertificateMutation();

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const smsCertificateSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await smsCertificate({
          input: {
            Type: 3,
            AuthenticationCode: values.authenticationCode,
            ProvisionalToken: provisionalToken,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.SMSCertificate?.Code === 0) {
          setPageStep(PageStep.FIN);
        }
      },
    },
    [smsCertificate, showToast, setPageStep],
  );

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit: smsCertificateSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setFieldValue('authenticationCode', '');
  }, []);

  return { formik, text: '送信された認証コードを入力してください', isUpdate: false };
};
